// What an S3-compatible endpoint says about its region. spec/03 §11a.
//
// Its own module rather than a helper inside AdminPage so it can be tested:
// it decides what requests are signed with, and a wrong guess produces a
// SignatureDoesNotMatch that reads like bad credentials.

/**
 * The region an S3-compatible endpoint implies, or null when it implies none.
 *
 * `region` and `endpoint` are not the same thing to the SDK — the endpoint is
 * the host a request goes to, and the region is the string baked into the
 * SigV4 credential scope it is signed with (`…/nyc3/s3/aws4_request`). Once an
 * endpoint is set the region picks no host at all.
 *
 * They only *look* redundant because DigitalOcean, Linode, Backblaze and
 * Wasabi all name the host after the region. MinIO's endpoint carries no
 * region (`http://127.0.0.1:9000`), and on AWS proper there is no endpoint to
 * type at all. So this fills the box in rather than removing it: the value
 * that gets signed with stays visible and editable, and a host that does not
 * announce its region leaves it blank rather than guessing.
 */
export function regionFromEndpoint(endpoint: string): string | null {
  let host: string;
  try {
    host = new URL(endpoint.trim()).hostname;
  } catch {
    return null;
  }

  const labels = host.split('.');
  // Needs a subdomain and a domain: `digitaloceanspaces.com` on its own names
  // no region, and neither does a bare host or an IP address.
  if (labels.length < 3) return null;
  if (/^\d+$/.test(labels.at(-1) ?? '')) return null;

  const first = labels[0]!;
  // A bucket-qualified host puts the bucket first — `signrs.nyc3.digitalocean…`
  // — so the region is the label before the provider rather than the first one.
  const candidate = labels.length > 3 ? labels[1]! : first;

  // Region names are lowercase letters, digits and hyphens, and always carry a
  // digit or a hyphen. Requiring one keeps `s3.amazonaws.com` and a plain
  // `storage.example.com` from being read as regions.
  return /^[a-z]{2,}[a-z0-9-]*[0-9-][a-z0-9-]*$/.test(candidate) ? candidate : null;
}
